import { useState, useEffect, type FC, type ChangeEvent } from 'react';
import { FormControl } from '@/features/trade/order/components/order-form-components';
import { Input } from '@/components/ui';
import {
  formatNumber,
  parseDecimalToBigInt,
  validatedDecimalStr,
} from '@/utils/value-format';
import { useMarketStore } from '@/store/use-markets-store';
import {
  getLimitPriceFromMaxSlippage,
  getSlippageReferencePrice,
} from '../utils/math';
import { useStore } from 'zustand';
import type { InputHTMLAttributes } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useFormContext } from 'react-hook-form';
import { OrderFormSchema } from './order-form';

interface MaxSlippageInputProps extends InputHTMLAttributes<HTMLInputElement> {
  symbol: string;
  isBuy: boolean;
  isSubmitSuccessful?: boolean;
}

export const getDefaultMaxSlippageValue = (symbol: string) => {
  switch (symbol) {
    case 'BTC-PERP':
    case 'ETH-PERP':
    case 'SOL-PERP':
      return 0.3;
    default:
      return 1;
  }
};

export const MaxSlippageInput: FC<MaxSlippageInputProps> = ({
  symbol,
  isBuy,
  isSubmitSuccessful = false,
}) => {
  const form = useFormContext<OrderFormSchema>();
  const { referencePrice, priceDecimals } = useStore(
    useMarketStore(symbol),
    useShallow((state) => ({
      referencePrice: getSlippageReferencePrice(symbol, isBuy),
      priceDecimals: state.marketSpec.priceDecimals,
    })),
  );

  const [maxSlippageInput, setMaxSlippageInput] = useState<string>('');

  useEffect(() => {
    setMaxSlippageInput('');
  }, [symbol]);

  const handleMaxSlippageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const validatedValue = validatedDecimalStr(e.target.value, 2, 4);
    if (Number(validatedValue) > 100) return;
    setMaxSlippageInput(validatedValue);
  };

  useEffect(() => {
    if (!referencePrice) {
      form.setValue('order.limitPrice', '');
      return;
    }

    let maxSlippagePct: bigint;
    if (maxSlippageInput === '') {
      maxSlippagePct = parseDecimalToBigInt(
        getDefaultMaxSlippageValue(symbol).toString(),
        2n,
      );
    } else {
      maxSlippagePct = parseDecimalToBigInt(maxSlippageInput, 2n); // 2 decimal precision of the percent
    }
    const limitPrice = getLimitPriceFromMaxSlippage(
      isBuy,
      maxSlippagePct,
      referencePrice,
      priceDecimals,
    ).decimal;

    form.setValue('order.limitPrice', limitPrice);
  }, [
    symbol,
    isBuy,
    maxSlippageInput,
    referencePrice,
    priceDecimals,
    isSubmitSuccessful,
  ]);

  return (
    <>
      <FormControl>
        <Input
          className="max-w-[7ch] rounded-none bg-vestgrey-800 font-mono text-base text-foreground transition-colors group-hover:bg-border"
          placeholder={formatNumber(getDefaultMaxSlippageValue(symbol), {
            digits: 2,
          })}
          value={maxSlippageInput}
          onChange={handleMaxSlippageChange}
        />
      </FormControl>
      <p className="px-2">%</p>
    </>
  );
};
